import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import LoadingSpinner from '../common/LoadingSpinner';
import { searchService } from '../../services/searchService';

const PremiseSearchCard = ({
  setPremiseResults,
  setShowResults,
}) => {

  // Premise search state
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [street, setStreet] = useState('');
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState('');

    // Load states on mount
  useEffect(() => {
    const loadStates = async () => {
      try {
        const stateList = await searchService.getStates();
        setStates(stateList);
      } catch (error) {
        console.error('Error loading states:', error);
      }
    };
    loadStates();
  }, []);

  // Load cities when state changes
  useEffect(() => {
    const loadCities = async () => {
      if (selectedState) {
        try {
          const cityList = await searchService.getCities(selectedState);
          setCities(cityList);
          setSelectedCity('');
        } catch (error) {
          console.error('Error loading cities:', error);
          setCities([]);
        }
      } else {
        setCities([]);
        setSelectedCity('');
      }
    };
    loadCities();
  }, [selectedState]);

  // Handle premise search
  const handlePremiseSearch = async (e) => {
    e.preventDefault();
    setValidated(true);
    setError('');
    setShowResults(false);

    const form = e.currentTarget;

    if (!form.checkValidity()) {
      setValidated(true);
      return;
    }

    setLoading(true);
    try {
      const response = await searchService.searchByPremise(
        selectedState,
        selectedCity,
        houseNumber,
        street
      );
      setPremiseResults(response.results);
      setShowResults(true);

    } catch (error) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card className="shadow">
      <Card.Header>
        <h5 className="mb-0">Search by Service Address</h5>
      </Card.Header>
      <Card.Body>
        <Form noValidate validated={validated} onSubmit={handlePremiseSearch}>
          <Form.Group className="mb-3">
            <InputGroup>
              <Form.FloatingLabel label="Premise State" controlId="state">
                <Form.Select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  required
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">Please select a state.</Form.Control.Feedback>
              </Form.FloatingLabel>
            </InputGroup>
          </Form.Group>
          <Form.Group className="mb-3">
            <InputGroup>
              <Form.FloatingLabel label="Premise City" controlId="city">
                <Form.Select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  disabled={!selectedState}
                  required
                >
                  <option value="">Select City</option>
                  {cities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">Please select a city.</Form.Control.Feedback>
              </Form.FloatingLabel>
            </InputGroup>
          </Form.Group>
          <Row>
            <Col md={4} className="mb-3">
              <Form.FloatingLabel label="House Number" controlId="house-number">
                <Form.Control
                  type="text"
                  value={houseNumber}
                  disabled={!selectedState || !selectedCity}
                  onChange={(e) => setHouseNumber(e.target.value)}
                  placeholder=""
                  required
                />
              </Form.FloatingLabel>
            </Col>
            <Col md={8} className="mb-3">
              <Form.FloatingLabel label="Street Name" controlId="street">
                <Form.Control
                  type="text"
                  value={street}
                  disabled={!selectedState || !selectedCity}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder=""
                  required
                />
              </Form.FloatingLabel>
            </Col>
          </Row>
          <Button
            type="submit"
            variant="primary"
            className="w-100"
            disabled={loading}
          >
            {loading ? (
              <LoadingSpinner inline message="Searching..." />
            ) : (
              'Search'
            )}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default PremiseSearchCard;
